package com.essntl.core.stripe.data

import com.essntl.config.EssntlConfig
import com.essntl.core.network.IO
import com.essntl.core.stripe.data.response.PaymentLinkDto
import com.essntl.core.stripe.data.response.PriceDto
import com.essntl.core.stripe.data.response.ProductDto
import com.essntl.core.stripe.domain.StripeClient
import com.essntl.core.stripe.domain.models.PaymentLink
import com.essntl.core.stripe.domain.models.Price
import com.essntl.core.stripe.domain.models.Product
import com.essntl.core.utils.repository.Repository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.bearerAuth
import io.ktor.client.request.forms.*
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.http.*
import io.ktor.utils.io.InternalAPI
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext

class StripeClientImpl(
    private val httpClient: HttpClient,
) : Repository(), StripeClient {
    override suspend fun createProduct(name: String): Result<Product> =
        runCatching {
            withContext(Dispatchers.IO) {
                httpClient.post("$BASE_URL/products") {
                    header(HttpHeaders.ContentType, ContentType.Application.FormUrlEncoded)
                    bearerAuth(EssntlConfig.stripeKey)
                    url {
                        parameters.append("name", name)
                    }
                }.body<ProductDto>().toModel()
            }
        }

    override suspend fun createPrice(
        currency: String,
        productId: String,
        unitAmount: Int,
    ): Result<Price> =
        runCatching {
            withContext(Dispatchers.IO) {
                httpClient.post("$BASE_URL/prices") {
                    header(HttpHeaders.ContentType, ContentType.Application.FormUrlEncoded)
                    bearerAuth(EssntlConfig.stripeKey)
                    url {
                        parameters.append("currency", currency)
                        parameters.append("product", productId)
                        parameters.append("unit_amount", unitAmount.toString())
                    }
                }
            }.body<PriceDto>().toModel()
        }

    override suspend fun createPaymentLink(
        priceIds: List<String>,
        quantity: Int,
        metadata: PaymentLink.Metadata,
    ): Result<PaymentLink> =
        runCatching {
            withContext(Dispatchers.IO) {
                httpClient.post("$BASE_URL/payment_links") {
                    header(HttpHeaders.ContentType, ContentType.Application.FormUrlEncoded)
                    bearerAuth(EssntlConfig.stripeKey)
                    url {
                        priceIds.forEachIndexed { index, priceId ->
                            parameters.append("line_items[$index][price]", priceId)
                            parameters.append("line_items[$index][quantity]", quantity.toString())
                        }
                        if (!metadata.client.isNullOrEmpty())
                            parameters.append("payment_intent_data[metadata[client]]", metadata.client)

                        if (!metadata.travelAgent.isNullOrEmpty())
                            parameters.append("payment_intent_data[metadata[travelAgent]]", metadata.travelAgent)

                        if (!metadata.proposal.isNullOrEmpty())
                            parameters.append("payment_intent_data[metadata[proposal]]", metadata.proposal)

                        if (!metadata.invoiceId.isNullOrEmpty())
                            parameters.append("payment_intent_data[metadata[invoiceId]]", metadata.invoiceId)

                        if (!metadata.qbInvoiceId.isNullOrEmpty())
                            parameters.append("payment_intent_data[metadata[qbInvoiceId]]", metadata.qbInvoiceId)

                        parameters.append("payment_intent_data[metadata[product]]", metadata.product)
                        parameters.append("restrictions[completed_sessions][limit]", "1")
                    }
                }
            }.body<PaymentLinkDto>().toModel()
        }

    @OptIn(InternalAPI::class)
    override suspend fun deactivatePaymentLink(paymentLinkId: String, active: Boolean): Result<PaymentLink> =
        runCatching {
            withContext(Dispatchers.IO) {
                httpClient.post("$BASE_URL/payment_links/$paymentLinkId") {
                    header(HttpHeaders.ContentType, ContentType.Application.FormUrlEncoded)
                    bearerAuth(EssntlConfig.stripeKey)
                    body = FormDataContent(
                        Parameters.build {
                            append("active", "$active")
                        },
                    )
                }
            }.body<PaymentLinkDto>().toModel()
        }

    companion object {
        private const val BASE_URL = "https://api.stripe.com/v1"
    }
}
